// src/helpers/pdfTratamientoHelper.js
import jsPDF from "jspdf";
import QRCode from "qrcode";
import { API_URL } from '../services/api/config';
import { getAuthHeaders } from '../services/api/utils/fetchWrapper';

/**
 * Genera un PDF compacto con el plan de tratamiento incluyendo QR en header
 * @param {string} tratamientoId - ID del tratamiento
 * @param {Object} dashboard - Datos del dashboard
 * @param {string} nombreMascota - Nombre de la mascota (opcional)
 */
export async function generarPDFTratamiento(tratamientoId, dashboard, nombreMascota = null) {
  const { tratamiento, progreso } = dashboard;

  // 1. Obtener token público y nombre de mascota si no se pasó
  let tokenPublico;
  let objetoName = nombreMascota;

  try {
    // Usar getAuthHeaders para obtener el token correcto
    const headers = await getAuthHeaders();

    const response = await fetch(`${API_URL}/api/tratamientos/${tratamientoId}/generar-token`, {
      method: 'POST',
      headers
    });
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Error generando token');
    }

    tokenPublico = data.token;

    // Si no tenemos nombre, intentar obtenerlo del endpoint público
    if (!objetoName && tokenPublico) {
      try {
        const publicResp = await fetch(`${API_URL}/api/tratamientos/${tratamientoId}/publico?token=${tokenPublico}`);
        const publicData = await publicResp.json();
        if (publicData.nombreMascota) {
          objetoName = publicData.nombreMascota;
        }
      } catch (e) {
        console.warn('No se pudo obtener nombre de mascota:', e);
      }
    }
  } catch (error) {
    console.error('Error obteniendo token:', error);
    throw new Error('No se pudo generar el enlace público');
  }

  objetoName = objetoName || 'Mascota';

  // 2. Generar URL y QR antes de crear el PDF
  const baseUrl = window.location.origin;
  const publicUrl = `${baseUrl}/tratamiento-publico/${tratamientoId}?token=${tokenPublico}`;

  const qrDataUrl = await QRCode.toDataURL(publicUrl, {
    width: 200,
    margin: 1,
    color: { dark: '#333333', light: '#ffffff' }
  });

  // 3. Crear documento PDF compacto
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "letter" // 612x792 pt
  });

  const pageWidth = 612;
  const margin = 30;
  const contentWidth = pageWidth - (margin * 2);

  // Colores
  const primaryColor = [0, 188, 212]; // #00BCD4
  const textColor = [51, 51, 51];
  const mutedColor = [102, 102, 102];

  // ============ HEADER CON QR ============
  const headerHeight = 85;
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, headerHeight, 'F');

  // Logo y título (lado izquierdo)
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  doc.text("LemmonTags", margin, 35);

  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.text("Plan de Tratamiento", margin, 52);

  // Nombre de la mascota
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(`Paciente: ${objetoName}`, margin, 72);

  // QR en esquina superior derecha (85x85 pt ≈ 30mm)
  const qrSize = 70;
  const qrX = pageWidth - margin - qrSize - 5;
  const qrY = 8;

  // Fondo blanco para el QR
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(qrX - 3, qrY - 3, qrSize + 6, qrSize + 6, 4, 4, 'F');
  doc.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);

  let y = headerHeight + 15;

  // ============ INFO DE PROGRESO ============
  doc.setFillColor(248, 249, 250);
  doc.roundedRect(margin, y, contentWidth, 70, 6, 6, 'F');

  // Fechas en una línea
  const fechaInicio = formatearFechaPDF(tratamiento.fechaInicio);
  const fechaFin = formatearFechaPDF(tratamiento.fechaFin);

  doc.setTextColor(...mutedColor);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Inicio: ${fechaInicio}`, margin + 12, y + 20);
  doc.text(`Fin: ${fechaFin}`, margin + 160, y + 20);
  doc.text(`Dia ${tratamiento.diaActual} de ${tratamiento.diasTotales}`, margin + 300, y + 20);

  // Progreso
  doc.setTextColor(...textColor);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text(`Progreso: ${progreso.porcentaje}%`, margin + 12, y + 42);

  // Barra de progreso
  const barX = margin + 100;
  const barWidth = contentWidth - 120;
  const barHeight = 14;
  doc.setFillColor(229, 231, 235);
  doc.roundedRect(barX, y + 32, barWidth, barHeight, 7, 7, 'F');

  const progressWidth = (progreso.porcentaje / 100) * barWidth;
  if (progressWidth > 0) {
    doc.setFillColor(...primaryColor);
    doc.roundedRect(barX, y + 32, Math.max(progressWidth, 14), barHeight, 7, 7, 'F');
  }

  y += 85;

  // ============ MEDICAMENTOS (compacto) ============
  doc.setFillColor(...primaryColor);
  doc.roundedRect(margin, y, contentWidth, 22, 4, 4, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("MEDICAMENTOS", margin + 12, y + 15);

  y += 30;

  // Lista de medicamentos en formato compacto
  doc.setTextColor(...textColor);

  if (tratamiento.medicamentos && tratamiento.medicamentos.length > 0) {
    for (const med of tratamiento.medicamentos) {
      const horarios = calcularHorariosPDF(med.frecuenciaHoras);
      const horariosStr = horarios.map(h => formatearHoraPDF(h)).join(', ');

      // Card compacta
      doc.setFillColor(250, 250, 250);
      const hasInstructions = med.instrucciones && med.instrucciones.length > 0;
      const cardHeight = hasInstructions ? 52 : 38;
      doc.roundedRect(margin, y, contentWidth, cardHeight, 4, 4, 'F');

      // Nombre y dosis
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(...textColor);
      const nombreDosis = med.dosis ? `${med.nombre} - ${med.dosis}` : med.nombre;
      doc.text(nombreDosis, margin + 10, y + 15);

      // Horarios
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...mutedColor);
      doc.text(`Horarios: ${horariosStr}  |  ${med.duracionDias} dias`, margin + 10, y + 28);

      // Instrucciones (si hay)
      if (hasInstructions) {
        doc.setFontSize(8);
        const instrText = med.instrucciones.length > 80
          ? med.instrucciones.substring(0, 77) + '...'
          : med.instrucciones;
        doc.text(`${instrText}`, margin + 10, y + 42);
      }

      y += cardHeight + 6;
    }
  } else {
    doc.setTextColor(...mutedColor);
    doc.setFontSize(10);
    doc.text("No hay medicamentos registrados", margin + 12, y + 15);
    y += 30;
  }

  y += 10;

  // ============ FOOTER COMPACTO ============
  // Línea separadora
  doc.setDrawColor(229, 231, 235);
  doc.line(margin, y, pageWidth - margin, y);

  y += 15;

  // Texto sobre el QR
  doc.setTextColor(...textColor);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text("Escanea el QR en la esquina superior para ver el progreso en tiempo real", margin, y);

  y += 20;

  // Fecha generación y sitio web
  doc.setTextColor(...mutedColor);
  doc.setFontSize(8);

  const fechaGenerado = new Date().toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
  doc.text(`Generado: ${fechaGenerado}`, margin, y);

  const lemmonText = "LemmonTags.com";
  const lemmonWidth = doc.getTextWidth(lemmonText);
  doc.text(lemmonText, pageWidth - margin - lemmonWidth, y);

  // 4. Guardar PDF
  const nombreArchivo = `tratamiento-${objetoName.replace(/\s+/g, '-')}.pdf`;
  doc.save(nombreArchivo);

  return { success: true, filename: nombreArchivo };
}

/**
 * Formatea fecha para el PDF (compacto)
 */
function formatearFechaPDF(fechaStr) {
  if (!fechaStr) return 'N/A';
  const fecha = new Date(fechaStr + 'T12:00:00');
  return fecha.toLocaleDateString('es-MX', {
    day: 'numeric',
    month: 'short'
  });
}

/**
 * Calcula horarios sugeridos basados en frecuencia
 */
function calcularHorariosPDF(frecuenciaHoras) {
  const horarios = {
    24: ['08:00'],
    12: ['08:00', '20:00'],
    8: ['06:00', '14:00', '22:00'],
    6: ['06:00', '12:00', '18:00', '00:00'],
    4: ['06:00', '10:00', '14:00', '18:00', '22:00', '02:00']
  };
  return horarios[frecuenciaHoras] || horarios[12];
}

/**
 * Formatea hora a formato 12h corto
 */
function formatearHoraPDF(hora) {
  if (!hora) return '';
  const [h] = hora.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'pm' : 'am';
  const hour12 = hour % 12 || 12;
  return `${hour12}${ampm}`;
}
